import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import moment from "moment";
import { environment } from "../../../environments/environment";
import {
  AdminUser,
  UpdateAdministratorTrialAssignmentRequest,
  UpdateAdminUserRequest
} from "../models/admin-user.model";
import { ChangeLog } from "../../shared/change-log/change-log.model";
import { AdminUserList } from "../models/admin-user-list.model";
import { Observable } from 'rxjs';
import { SelectOption } from "../models/select-option.model";

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  currentPageNumber = 1;
  private httpNoInterceptors: HttpClient;

  constructor(private http: HttpClient, handler: HttpBackend) {
    this.httpNoInterceptors = new HttpClient(handler);
  }


  getMatrix() {
    return this.http.get(environment.apiUrl + '/admin/matrix');
  }

  /**
   * Exports admin and site users
   * @param dateFrom
   * @param dateTo
   */
  exportUsers(dateFrom: string, dateTo: string) {
    let body = {
      dateFrom: dateFrom ? moment(dateFrom + ' 00:00', 'DD/MM/YYYY HH:mm').format('YYYY-MM-DDTHH:mm:ssZ') : null,
      dateTo: dateTo ? moment(dateTo + ' 23:59', 'DD/MM/YYYY HH:mm').format('YYYY-MM-DDTHH:mm:ssZ') : null
    }

    return this.http.post(environment.apiUrl + '/admin/export', body);
  }

  /**
   * Export admin change log
   * @param dateFrom
   * @param dateTo
   */
  exportChangeLog(dateFrom: string, dateTo: string) {
    let body = {
      dateFrom: dateFrom ? moment(dateFrom + ' 00:00', 'DD/MM/YYYY HH:mm').format('YYYY-MM-DDTHH:mm:ssZ') : null,
      dateTo: dateTo ? moment(dateTo + ' 23:59', 'DD/MM/YYYY HH:mm').format('YYYY-MM-DDTHH:mm:ssZ') : null
    }

    return this.http.post(environment.apiUrl + '/admin/changelog/export', body);
  }

  /**
   * Get the admin change log
   * @param page
   */
  changeLog(page = 1) {
    let params = new HttpParams().set('page', page.toString());

    return this.http.get<ChangeLog>(environment.apiUrl + '/admin/changelog', { params: params }).pipe(map(rsp => {
      return new ChangeLog(rsp);
    }));
  }

  /**
   * Deactivates an admin user
   * @param id
   */
  deactivateAdminUser(id: string) {
    return this.http.post(environment.apiUrl + '/admin/' + id + '/deactivate', {});
  }

  /**
   * Retrieve a list of admin users
   * @param currentPage the page number of results to return
   * @param pageSize
   * @param roleNames
   * @param keywords
   */
  retrieveAdminUsers(currentPage = 1, pageSize = 0, roleNames: string = null, keywords: string = null) {
    let params = new HttpParams().set('page', currentPage.toString());
    if (pageSize > 0) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (roleNames) {
      params = params.set('roleNames', roleNames);
    }
    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http.get<AdminUserList>(environment.apiUrl + '/admin', { params: params });
  }

  /**
   * Search for admin users by name, for use in an autocomplete
   * @param searchTerm
   */
  suggestAdmin(searchTerm: string): Observable<SelectOption[]> {
    const params = new HttpParams().set('searchTerm', searchTerm);

    return this.http.get<SelectOption[]>(environment.apiUrl + '/admin/suggest', { params: params });
  }

  /**
   * Retrieve a single admin user
   * @param id admin Id
   */
  retrieveAdminUser(id: string) {
    return this.http.get<AdminUser>(environment.apiUrl + '/admin/' + id);
  }

  /**
   * Updates an existing administrator
   */
  updateAdminUser(request: UpdateAdminUserRequest) {
    if (!request.assignToAllTrials)
      request.assignToAllTrials = false;

    if (!request.unassignFromAllTrials)
      request.unassignFromAllTrials = false;

    return this.http.put(environment.apiUrl + '/admin', request);
  }

  /**
   * Creates a new admin user
   * @param firstname admin users firstname
   * @param lastname admin users lastname
   * @param email admin users email address
   * @param roles an array containing a list of roles to be applied to the account
   * @param trialIds
   * @param projectManagerTrialIds
   * @param muvUserId
   * @param assignToAllTrials
   * @param unassignFromAllTrials
   */
  createAdminUser(firstname: string, lastname: string, email: string, roles: string[], trialIds: string[], projectManagerTrialIds: string[], muvUserId: string, assignToAllTrials: boolean = false, unassignFromAllTrials: boolean = false, canBypassEntra: boolean = false, password: string = null) {
    return this.http.post(environment.apiUrl + '/admin', {
      Firstname: firstname,
      Lastname: lastname,
      Email: email,
      Roles: roles,
      TrialIds: trialIds,
      projectManagerTrialIds: projectManagerTrialIds,
      muvUserId: muvUserId,
      assignToAllTrials: assignToAllTrials,
      unassignFromAllTrials: unassignFromAllTrials,
      canBypassEntra: canBypassEntra,
      password: password
    });
  }

  updateTrialAdministratorAssignment(request: UpdateAdministratorTrialAssignmentRequest): Observable<void> {
    return this.http.put<void>(environment.apiUrl + '/admin/assignment', request);
  }

  isBypassAuthEnabled(): Observable<{ enabled: boolean }> {
    return this.httpNoInterceptors.get<{ enabled: boolean }>(environment.apiUrl + '/admin/bypass-auth-enabled');
  }

  bypassLogin(email: string, password: string) {
    return this.httpNoInterceptors.post<{ token: string, expiration: string }>(environment.apiUrl + '/admin/bypass-login', {
      email: email,
      password: password
    });
  }
}
